import { AnalysisFeedbackPayload } from "@risalabs_frontend_org/oasis-ui-kit";
import { FormEvent, useState } from "react";
import { FEEDBACK_CATEGORIES } from "./feedbackCategories";
import FeedbackCategoryInfoIcon from "./feedbackCategoryInfoIcon";

/** Shown when the reviewer saves without attributing a category. */
const NO_SELECTION_ERROR = "Please select at least one option.";

export interface UserFeedBackOnAnswerChangeProps {
  /**
   * Called when the reviewer submits the feedback form. Receives the full
   * payload (selected category values + free-text comments). The parent is
   * responsible for posting it to the backend, closing the modal, etc.
   */
  onSubmit?: (payload: AnalysisFeedbackPayload) => void;
  /**
   * Called when the reviewer dismisses the form without submitting (e.g.
   * clicks "Cancel"). The parent typically closes the surrounding overlay.
   */
  onCancel?: () => void;
}

/**
 * Feedback form shown when a reviewer overrides / changes the questionnaire's
 * suggested answer. Owns the in-flight selection + comments state locally and
 * surfaces the final payload to the parent via {@link
 * UserFeedBackOnAnswerChangeProps.onSubmit}.
 *
 * Each category carries an info icon whose hover card explains what the
 * category means and which failure modes it covers.
 */
const UserFeedBackOnAnswerChange: React.FC<UserFeedBackOnAnswerChangeProps> = ({
  onSubmit,
  onCancel,
}) => {
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [comments, setComments] = useState("");
  const [error, setError] = useState("");

  const toggleValue = (value: string) => {
    setError("");
    setSelectedValues((previous) =>
      previous.includes(value)
        ? previous.filter((selected) => selected !== value)
        : [...previous, value],
    );
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (selectedValues.length === 0) {
      setError(NO_SELECTION_ERROR);
      return;
    }
    onSubmit?.({ selectedValues, comments });
  };

  const handleCancel = () => {
    setSelectedValues([]);
    setComments("");
    setError("");
    onCancel?.();
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex w-full flex-col items-start gap-5 rounded bg-[#FFFDE6] p-3"
    >
      <p className="m-0 text-sm font-semibold leading-5 text-black">
        Which of the analysis is wrong
      </p>

      <div className="flex w-full flex-col gap-3">
        {FEEDBACK_CATEGORIES.map((category) => (
          <div
            key={category.value}
            className="flex w-full items-center justify-between gap-3"
          >
            <label className="flex cursor-pointer items-center gap-2 text-xs font-normal leading-4 text-[#2E2E2E]">
              <input
                type="checkbox"
                checked={selectedValues.includes(category.value)}
                onChange={() => toggleValue(category.value)}
                className="h-4 w-4 cursor-pointer rounded border border-[#C4C4C4] accent-black"
              />
              <span>{category.label}</span>
            </label>
            <FeedbackCategoryInfoIcon category={category} />
          </div>
        ))}
      </div>

      <div className="flex w-full flex-col items-start gap-1">
        <label
          htmlFor="analysis-feedback-comments"
          className="text-xs font-medium leading-4 text-[#2E2E2E]"
        >
          Comments
        </label>
        <input
          id="analysis-feedback-comments"
          type="text"
          value={comments}
          onChange={(event) => setComments(event.target.value)}
          placeholder="Please describe what went wrong with the AI analysis..."
          className="h-9 w-full rounded border border-[#E5E5E5] bg-white px-3 text-xs font-normal leading-4 text-[#2E2E2E] placeholder:text-[#8C8C8C] focus:border-[#8C8C8C] focus:outline-none"
        />
      </div>

      {error && <p className="m-0 text-xs leading-4 text-[#D92D20]">{error}</p>}

      <div className="flex w-full items-center justify-end gap-2">
        <button
          type="button"
          onClick={handleCancel}
          className="inline-flex h-7 items-center justify-center rounded border border-[#0F0F0F] px-4 text-xs font-medium leading-4 text-[#0F0F0F] transition-colors hover:bg-[#F5F5F5]"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="inline-flex h-7 items-center justify-center rounded bg-[#0F0F0F] px-4 text-xs font-medium leading-4 text-white transition-colors hover:bg-[#2E2E2E]"
        >
          Save
        </button>
      </div>
    </form>
  );
};

export default UserFeedBackOnAnswerChange;
